import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Label, FormGroup } from 'reactstrap';
import styled from 'styled-components';
import useUser from '../../../customHooks/useUser';
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalFooter } from 'reactstrap';
import { CargoSpectreScanner, ICorpStation } from '../../../globals/interfaces';
import { createdUpdatedInfo, getTsDate } from '../../../utils';
import BackButton from '../../../components/custom/BackButton';
import { ZodFormProvider, useZodForm } from '../../../components/custom/ZodForm/ZodFormProvider';
import { createZodInput } from '../../../components/custom/ZodForm/TypedInput';
import ActionIcon from '../../../components/custom/ActionIcon';
import { CargoSpectreScannerSchema, CargoSpectreScannerSchemaUpdate } from './validation';
import { createZodSelect } from '../../../components/custom/ZodForm/ZodSelect';
import dayjs from 'dayjs';

const FormInput = createZodInput<CargoSpectreScanner>();
const Select = createZodSelect<CargoSpectreScanner>();

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    record?: CargoSpectreScanner;
    onSubmit: (data: CargoSpectreScanner, update?: boolean) => void;
    deleteItem: (id: number) => Promise<void>;
    stations: Array<ICorpStation>;
}

export default function CargoSpectreModal({
    modal,
    setModal,
    record,
    onSubmit,
    deleteItem,
    stations
}: Props) {

    const { user } = useUser();
    const update = Boolean(record);

    // Create form with strict validation options
    const methods = useZodForm<CargoSpectreScanner>({
        resolver: update ? zodResolver(CargoSpectreScannerSchemaUpdate) : zodResolver(CargoSpectreScannerSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        shouldFocusError: true,
        // Ensure number fields are properly converted
        context: { valueAsNumber: true },
    });

    const {
        handleSubmit,
        reset,
        clearErrors,
        trigger,
        formState: { errors, isValid, isDirty },
        requiredFields
    } = methods;

    const units = stations.map(item => ({ label: item.s_unit, value: item.s_unit }));

    // Reset form when modal opens/closes or record changes
    useEffect(() => {
        if (modal) {
            // Clear all previous errors
            clearErrors();
            const now = getTsDate();

            if (record) {
                reset({
                    ...record,
                    modified: now,
                    modifiedBy: user.s_email,
                    lastCalibrated: dayjs(record.lastCalibrated).format('YYYY-MM-DD')
                });
            } else {
                reset({
                    name: '',
                    unit: '',
                    ipAddress: '',
                    lastCalibrated: '',
                    proxyEnabled: false,
                    notes: '',
                    created: now,
                    createdBy: user.s_email,
                    modified: now,
                    modifiedBy: user.s_email
                });
            }

            trigger();
        }
    }, [modal, record, reset, clearErrors, user.s_email]);

    const toggle = () => setModal(!modal);

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <div>
                        <BackButton onClick={toggle} />
                        <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} CargoSpectre Scanner</h4>
                    </div>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <ZodFormProvider methods={methods} requiredFields={requiredFields}>
                    <FlexContainer>
                        <div>
                            <FormInput label="Name (must match the settings)" name={'name'} />
                            <Select 
                                options={units}
                                name={'unit'}
                                label={'Unit'}
                            />
                            <FormInput label="Proxy Server Enabled" name={'proxyEnabled'} type={'checkbox'} />
                            <FormInput label="IP Address" name={'ipAddress'} />
                            <FormInput label="Last Calibrated" name={'lastCalibrated'} type={'date'} />
                            <FormInput label="Notes" name={'notes'} />
                        </div>
                    </FlexContainer>
                </ZodFormProvider>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label className={'d-block'}>
                        {createdUpdatedInfo(record, true)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteItem(record.id)}
                                    simulateLoading
                                />
                            )
                        }
                        <ActionIcon
                            type={'save'}
                            disabled={!isDirty || !isValid}
                            onClick={handleSubmit(data => onSubmit(data, update))}
                            simulateLoading
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </CustomModal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    & div {
        display: flex;
        align-items: center;
    }
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    width: 100%;

    & div {
        width: 100%;
    }
`;

const FooterFlex = styled.div`
    display: flex;
`;

const CustomModal = styled(Modal)`

`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;